/**
 * @file 資料の付属音声を Firebase Storage から取り、object URL にして使い回す。
 *
 * @what: path ごとに1回だけ getBytes し、blob の URL を LRU (3本) で持つ。取得中の
 *   Promise も共有するので、同じ音声を指す隣のページへ送っても取得は1回になる。
 * @why: この本は音声1本が4ページ (2フレーズ) に付くので、ページを送るたびに取り直すと
 *   同じ数MBを何度も落とすことになる。画像のキャッシュ (pageImageCache) と分けるのは、
 *   1本が数MBあり、画像と同じ16枚ぶんを持つと端末のメモリを使いすぎるためである。
 * @failure: 取得失敗はキャッシュに残さない (次に開いたときに取り直す)。いま鳴らしている
 *   音声は最後に触ったものなので、3本の上限では追い出されない。
 */

import { getBytes, ref as storageRef } from "firebase/storage";
import { getFirebaseStorage } from "@/shared/firebase/client";

type Entry = { promise: Promise<string>; url: string | null };

/** 手元に置く音声の本数 (いま鳴らしているものと、その前後ぶんを残す)。 */
const CAPACITY = 3;

/** Map の挿入順を LRU として使う (触るたびに delete → set で末尾へ移す)。 */
const cache = new Map<string, Entry>();

function evictOverflow(): void {
  for (const [key, entry] of cache) {
    if (cache.size <= CAPACITY) break;
    cache.delete(key);
    if (entry.url) URL.revokeObjectURL(entry.url);
    else entry.promise.then((url) => URL.revokeObjectURL(url)).catch(() => {});
  }
}

/** 音声を取得して object URL を返す。 */
export function loadPageAudio(audioPath: string, contentType: string): Promise<string> {
  const hit = cache.get(audioPath);
  if (hit) {
    cache.delete(audioPath);
    cache.set(audioPath, hit);
    return hit.promise;
  }
  const entry: Entry = { promise: Promise.resolve(""), url: null };
  entry.promise = getBytes(storageRef(getFirebaseStorage(), audioPath))
    .then((buf) => {
      const url = URL.createObjectURL(new Blob([buf], { type: contentType }));
      entry.url = url;
      return url;
    })
    .catch((err: unknown) => {
      cache.delete(audioPath);
      throw err;
    });
  cache.set(audioPath, entry);
  evictOverflow();
  return entry.promise;
}

/** 全部 revoke して空にする (テストと、資料を離れたあとの明示的な解放用)。 */
export function resetPageAudioCache(): void {
  for (const entry of cache.values()) {
    if (entry.url) URL.revokeObjectURL(entry.url);
    else entry.promise.then((url) => URL.revokeObjectURL(url)).catch(() => {});
  }
  cache.clear();
}
